import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    Alert,
    CircularProgress,
} from '@mui/material';
import { LockReset as LockResetIcon } from '@mui/icons-material';
import { giftcardService } from '../services/giftcardService';

const ResetPINModal = ({ open, onClose, giftcardNo, onSuccess }) => {
    const [newPIN, setNewPIN] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!newPIN.trim()) {
            setError('Please enter a new PIN');
            return;
        }

        setLoading(true);
        setError('');
        
        try {
            const response = await giftcardService.resetPIN(giftcardNo, newPIN.trim());
            
            if (response.success) {
                setSuccess(true);
                setTimeout(() => {
                    handleClose();
                    if (onSuccess) onSuccess();
                }, 1500);
            } else {
                setError(response.message || 'Failed to reset PIN');
            }
        } catch (err) {
            console.error('Error resetting PIN:', err);
            setError(err.response?.data?.message || 'Failed to reset PIN. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            setNewPIN('');
            setError('');
            setSuccess(false);
            onClose();
        }
    };

    const handleInputChange = (e) => {
        setNewPIN(e.target.value);
        if (error) setError('');
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LockResetIcon color="primary" />
                Reset PIN
            </DialogTitle>
            <DialogContent>
                <Box sx={{ py: 2 }}>
                    <Typography variant="body1" gutterBottom>
                        Reset PIN for giftcard: <strong>{giftcardNo}</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Enter the new PIN below. This will replace the current PIN for this giftcard.
                    </Typography>

                    {success && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            PIN reset successfully!
                        </Alert>
                    )}

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <TextField
                        fullWidth
                        label="New PIN"
                        value={newPIN}
                        onChange={handleInputChange}
                        error={!!error}
                        disabled={loading || success}
                        autoFocus
                        inputProps={{
                            maxLength: 50
                        }}
                        helperText="Enter the new PIN (no masking applied)"
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button 
                    onClick={handleClose} 
                    disabled={loading}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={loading || !newPIN.trim() || success}
                    startIcon={loading ? <CircularProgress size={16} /> : <LockResetIcon />}
                >
                    {loading ? 'Resetting...' : 'Reset PIN'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ResetPINModal;
